import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import { Card, Chip, Dialog, DialogContent, Grid, IconButton, MenuItem, Pagination, TextField } from "@mui/material";
import { Link } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSnackbar from "components/MDSnackbar";
import UserIdentityPreview from "components/UserIdentityPreview";
import agriPaginationSx from "components/AgriTheme/agriPaginationSx";

const getTodayDateInput = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSeconds = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Not recorded";
  }
  const totalSeconds = Number(value || 0);

  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) {
    return "Not recorded";
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const parts = [];

  if (hours) {
    parts.push(`${hours}h`);
  }

  if (minutes) {
    parts.push(`${minutes}m`);
  }

  if (seconds || !parts.length) {
    parts.push(`${seconds}s`);
  }

  return parts.join(" ");
};

const formatPoints = (value) => Number(value || 0).toLocaleString("en-IN");

const getUserImageUrl = (value) => {
  if (!value) {
    return "";
  }

  if (String(value).startsWith("http")) {
    return value;
  }

  return `${Constants.imageDomain}${value}`;
};

const buildPagerLabel = (pagination) => {
  if (!pagination?.totalDocs) {
    return "0 results";
  }

  const start = (pagination.page - 1) * pagination.limit + 1;
  const end = Math.min(pagination.page * pagination.limit, pagination.totalDocs);
  return `${start}-${end} of ${pagination.totalDocs}`;
};

const summaryCards = [
  { key: "unique_user_count", label: "Active Users", color: "primary" },
  { key: "practice_count", label: "Practice Tests", color: "info" },
  { key: "mock_count", label: "Mock Tests", color: "warning" },
  { key: "test_series_count", label: "Test Series", color: "success" },
  { key: "live_test_count", label: "Live Tests", color: "error" },
  { key: "total_points", label: "Total Points", color: "dark" },
];

const answerLabel = (key, value) => {
  const label = String(key || "").replace("option", "Option ");
  return [label, String(value || "").trim()].filter(Boolean).join(": ") || "Not available";
};

function AttemptReviewDialog({ open, onClose, loading, error, review, userName }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          color: "#edf4ff",
          borderRadius: "18px",
          border: "1px solid rgba(126,170,220,0.18)",
          background: "linear-gradient(180deg, #0d1d2f 0%, #07121f 100%)",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <MDBox
          px={{ xs: 2, md: 3 }}
          py={2.25}
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={2}
          sx={{ borderBottom: "1px solid rgba(126,170,220,0.14)" }}
        >
          <MDBox>
            <MDTypography variant="h5" sx={{ color: "#fff" }}>
              {review?.test_name || "Attempt Review"}
            </MDTypography>
            <MDTypography variant="button" display="block" mt={0.5} sx={{ color: "#9db4cf" }}>
              {[userName, review?.test_type_label, formatDate(review?.createdAt)]
                .filter((item) => item && item !== "-")
                .join(" • ")}
            </MDTypography>
          </MDBox>
          <IconButton aria-label="Close attempt review" onClick={onClose} sx={{ color: "#c7d8eb" }}>
            <CloseIcon />
          </IconButton>
        </MDBox>

        <MDBox p={{ xs: 2, md: 3 }}>
          {loading ? (
            <MDTypography variant="button" sx={{ color: "#9db4cf" }}>
              Loading submitted answers...
            </MDTypography>
          ) : error ? (
            <MDTypography variant="button" sx={{ color: "#ffc3ca" }}>
              {error}
            </MDTypography>
          ) : review ? (
            <>
              <Grid container spacing={1.25} mb={2.5}>
                {[
                  { label: "Score", value: review.result ?? "-" },
                  { label: "Time Taken", value: formatSeconds(review.total_time_spent_seconds) },
                  { label: "Correct", value: review.rightAttempt || 0 },
                  { label: "Incorrect", value: review.wrongAttempt || 0 },
                  { label: "Skipped", value: review.skipAttempt || 0 },
                  { label: "Accuracy", value: `${Number(review.accuracyPercent || 0)}%` },
                ].map((item) => (
                  <Grid item xs={6} sm={4} md={2} key={item.label}>
                    <MDBox p={1.5} borderRadius="lg" sx={{ backgroundColor: "rgba(255,255,255,0.04)" }}>
                      <MDTypography variant="caption" sx={{ color: "#8fa7c4" }}>
                        {item.label}
                      </MDTypography>
                      <MDTypography variant="h6" mt={0.35} sx={{ color: "#fff" }}>
                        {item.value}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                ))}
              </Grid>

              <MDTypography variant="caption" display="block" mb={2} sx={{ color: "#8fa7c4" }}>
                Question times are reported by the app and are indicators, not proof of cheating.
              </MDTypography>

              <MDBox display="flex" flexDirection="column" gap={1.75}>
                {review.review_questions?.length ? review.review_questions.map((item) => (
                  <MDBox
                    key={`${review._id}-${item.order}`}
                    p={{ xs: 1.75, md: 2.25 }}
                    borderRadius="xl"
                    sx={{ border: "1px solid rgba(126,170,220,0.16)", backgroundColor: "rgba(17,35,56,0.75)" }}
                  >
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                        <MDTypography variant="h6" sx={{ color: "#fff" }}>
                          Question {item.order}
                        </MDTypography>
                        <Chip
                          label={item.is_skipped ? "Skipped" : item.is_correct ? "Correct" : "Incorrect"}
                          color={item.is_skipped ? "warning" : item.is_correct ? "success" : "error"}
                          size="small"
                        />
                        {Number(item.time_spent_seconds || 0) > 0 ? (
                          <Chip label={formatSeconds(item.time_spent_seconds)} size="small" />
                        ) : null}
                      </MDBox>
                      {item.can_edit && item.question_id ? (
                        <MDButton
                          component={Link}
                          to={`/updateQuestion/${item.question_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          variant="outlined"
                          color="info"
                          size="small"
                        >
                          Edit Question
                        </MDButton>
                      ) : null}
                    </MDBox>

                    <MDTypography variant="button" display="block" mt={1.5} sx={{ color: "#fff", whiteSpace: "pre-wrap" }}>
                      {item.question || "Question text is unavailable for this older attempt."}
                    </MDTypography>
                    {item.question_hindi ? (
                      <MDTypography variant="button" display="block" mt={0.5} sx={{ color: "#b8cbe0", whiteSpace: "pre-wrap" }}>
                        {item.question_hindi}
                      </MDTypography>
                    ) : null}
                    {item.assertion ? (
                      <MDTypography variant="button" display="block" mt={0.75} sx={{ color: "#edf4ff", whiteSpace: "pre-wrap" }}>
                        Assertion: {item.assertion}
                      </MDTypography>
                    ) : null}
                    {item.reason ? (
                      <MDTypography variant="button" display="block" mt={0.5} sx={{ color: "#edf4ff", whiteSpace: "pre-wrap" }}>
                        Reason: {item.reason}
                      </MDTypography>
                    ) : null}

                    <Grid container spacing={1} mt={0.5}>
                      {(item.options || []).filter((option) => option.text || option.text_hindi).map((option) => (
                        <Grid item xs={12} md={6} key={option.key}>
                          <MDBox
                            p={1.25}
                            borderRadius="lg"
                            sx={{
                              border: `1px solid ${option.is_correct_answer ? "rgba(69,196,106,0.65)" : option.is_user_answer ? "rgba(255,105,120,0.65)" : "rgba(126,170,220,0.15)"}`,
                            }}
                          >
                            <MDTypography variant="button" sx={{ color: "#edf4ff" }}>
                              {answerLabel(option.key, option.text)}
                            </MDTypography>
                            {option.is_user_answer ? <Chip label="User" size="small" color="info" sx={{ ml: 1 }} /> : null}
                            {option.is_correct_answer ? <Chip label="Correct" size="small" color="success" sx={{ ml: 1 }} /> : null}
                            {option.text_hindi && option.text_hindi !== option.text ? (
                              <MDTypography variant="caption" display="block" sx={{ color: "#9db4cf" }}>
                                {option.text_hindi}
                              </MDTypography>
                            ) : null}
                          </MDBox>
                        </Grid>
                      ))}
                    </Grid>

                    <Grid container spacing={1.25} mt={0.5}>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" sx={{ color: "#73b6ff" }}>
                          USER&apos;S ANSWER
                        </MDTypography>
                        <MDTypography variant="button" display="block" sx={{ color: "#edf4ff" }}>
                          {item.is_skipped ? "Skipped" : answerLabel(item.user_answer, item.user_answer_text)}
                        </MDTypography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <MDTypography variant="caption" sx={{ color: "#66d48a" }}>
                          CORRECT ANSWER
                        </MDTypography>
                        <MDTypography variant="button" display="block" sx={{ color: "#edf4ff" }}>
                          {answerLabel(item.correct_answer, item.correct_answer_text)}
                        </MDTypography>
                      </Grid>
                    </Grid>
                    <MDTypography variant="caption" display="block" mt={1.5} sx={{ color: "#9db4cf" }}>
                      DESCRIPTION / EXPLANATION
                    </MDTypography>
                    <MDTypography variant="button" display="block" sx={{ color: "#dbe8f6", whiteSpace: "pre-wrap" }}>
                      {item.description || "No description is available for this question."}
                    </MDTypography>
                    {item.description_hindi && item.description_hindi !== item.description ? (
                      <MDTypography variant="button" display="block" mt={0.5} sx={{ color: "#aebfd2", whiteSpace: "pre-wrap" }}>
                        {item.description_hindi}
                      </MDTypography>
                    ) : null}
                  </MDBox>
                )) : (
                  <MDTypography variant="button" sx={{ color: "#9db4cf" }}>
                    No submitted answers are stored for this attempt.
                  </MDTypography>
                )}
              </MDBox>
            </>
          ) : null}
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

AttemptReviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
  review: PropTypes.object,
  userName: PropTypes.string,
};

AttemptReviewDialog.defaultProps = {
  review: null,
  userName: "",
};

function AttemptSection({
  title,
  rows,
  pagination,
  onPageChange,
  onEntriesPerPageChange,
  renderMeta,
  emptyText,
  onViewAttempt,
}) {
  return (
    <Card
      sx={{
        background: "linear-gradient(180deg, rgba(11,22,36,0.96) 0%, rgba(9,18,31,0.96) 100%)",
        border: "1px solid rgba(126, 170, 220, 0.12)",
        boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
      }}
    >
      <MDBox p={2.5}>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          gap={1}
        >
          <MDTypography variant="h6" sx={{ color: "#edf4ff" }}>
            {title}
          </MDTypography>
          <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
            {buildPagerLabel(pagination)}
          </MDTypography>
        </MDBox>

        <MDBox mt={2} display="flex" flexDirection="column" gap={1.5}>
          {rows?.length ? (
            rows.map((row, index) => (
              <MDBox
                key={row._id || `${title}-${index}`}
                p={1.5}
                borderRadius="xl"
                sx={{
                  border: "1px solid rgba(126, 170, 220, 0.12)",
                  background:
                    "linear-gradient(180deg, rgba(17,35,56,0.8) 0%, rgba(13,29,47,0.78) 100%)",
                }}
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={1}
                  mb={1}
                >
                  <MDBox>
                    <MDTypography variant="button" fontWeight="bold" sx={{ color: "#edf4ff" }}>
                      #{(pagination?.page - 1 || 0) * (pagination?.limit || 10) + index + 1}{" "}
                      {row.test_name}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                      {formatDate(row.createdAt)}
                    </MDTypography>
                  </MDBox>

                  <MDBox textAlign={{ xs: "left", md: "right" }}>
                    <MDTypography variant="button" fontWeight="bold" sx={{ color: "#edf4ff" }}>
                      {formatPoints(row.points)} pts
                    </MDTypography>
                    <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                      Time taken: {formatSeconds(row.total_time_spent_seconds)}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <Grid container spacing={1.25}>
                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                      User
                    </MDTypography>
                    <UserIdentityPreview
                      name={row.user_name}
                      imageUrl={getUserImageUrl(row.user_profile_pic_url)}
                      imageSize={38}
                    />
                    {row.user_id ? (
                      <Link to={`/user-details/${row.user_id}`}>
                        <MDButton variant="text" color="info" size="small" sx={{ px: 0, mt: 0.5 }}>
                          View User
                        </MDButton>
                      </Link>
                    ) : null}
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                      Attempt Summary
                    </MDTypography>
                    <MDTypography variant="button" display="block" sx={{ color: "#edf4ff" }}>
                      Right: {row.right_attempt} | Wrong: {row.wrong_attempt} | Skip:{" "}
                      {row.skip_attempt}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" sx={{ color: "#8fa7c4" }}>
                      Questions: {row.total_questions} • Accuracy: {row.accuracy_percent}%
                    </MDTypography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MDTypography variant="caption" sx={{ color: "#8fa7c4" }} fontWeight="bold">
                      Details
                    </MDTypography>
                    <MDTypography variant="button" display="block" sx={{ color: "#edf4ff" }}>
                      {renderMeta(row)}
                    </MDTypography>
                  </Grid>
                </Grid>
                {row.user_id && row._id ? (
                  <MDBox mt={1.5}>
                    <MDButton variant="outlined" color="info" size="small" onClick={() => onViewAttempt(row)}>
                      View Attempt
                    </MDButton>
                  </MDBox>
                ) : null}
              </MDBox>
            ))
          ) : (
            <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
              {emptyText}
            </MDTypography>
          )}
        </MDBox>

        <MDBox
          mt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
        >
          <MDBox display="flex" alignItems="center" gap={1.25}>
            <MDTypography variant="caption" sx={{ color: "#c7d8eb" }}>
              Rows per page
            </MDTypography>
            <TextField
              select
              size="small"
              value={pagination?.limit || 10}
              onChange={(event) => onEntriesPerPageChange(Number(event.target.value))}
              sx={{ width: 82 }}
            >
              {[10, 25, 50, 100].map((size) => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </TextField>
          </MDBox>
          {pagination?.totalPages > 1 ? (
            <Pagination
              page={pagination.page}
              count={pagination.totalPages}
              color="primary"
              variant="outlined"
              shape="rounded"
              sx={agriPaginationSx}
              onChange={(event, page) => onPageChange(page)}
            />
          ) : null}
        </MDBox>
      </MDBox>
    </Card>
  );
}

AttemptSection.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  pagination: PropTypes.shape({
    page: PropTypes.number,
    limit: PropTypes.number,
    totalDocs: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  onEntriesPerPageChange: PropTypes.func,
  renderMeta: PropTypes.func.isRequired,
  emptyText: PropTypes.string.isRequired,
  onViewAttempt: PropTypes.func.isRequired,
};

AttemptSection.defaultProps = {
  rows: [],
  pagination: null,
  onPageChange: () => {},
  onEntriesPerPageChange: () => {},
};

export default function UserAttemptReports() {
  const today = useMemo(() => getTodayDateInput(), []);
  const [filters, setFilters] = useState({
    start_date: today,
    end_date: today,
    search: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    start_date: today,
    end_date: today,
    search: "",
  });
  const [pages, setPages] = useState({
    practice: 1,
    mock: 1,
    testSeries: 1,
    liveTest: 1,
  });
  const [limits, setLimits] = useState({
    practice: 10,
    mock: 10,
    testSeries: 10,
    liveTest: 10,
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [notiColor, setNotiColor] = useState("success");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [review, setReview] = useState(null);
  const [reviewUserName, setReviewUserName] = useState("");
  const reviewRequestId = useRef(0);

  const closeReview = () => {
    reviewRequestId.current += 1;
    setReviewOpen(false);
  };

  const openAttempt = async (type, row) => {
    const requestId = ++reviewRequestId.current;
    setReviewOpen(true);
    setReviewLoading(true);
    setReviewError("");
    setReview(null);
    setReviewUserName(row.user_name || "Unknown User");

    if (init() !== "success") {
      setReviewLoading(false);
      setReviewError("Your admin session has expired. Please sign in again.");
      return;
    }

    try {
      const response = await axios.get(
        `${Constants.getUrls.getUserAttemptReports}/${type}/${row.user_id}/${row._id}`
      );
      if (reviewRequestId.current === requestId) setReview(response?.data?.data || null);
    } catch (error) {
      if (reviewRequestId.current === requestId) {
        setReviewError(error?.response?.data?.message || "Unable to load this attempt.");
      }
    } finally {
      if (reviewRequestId.current === requestId) setReviewLoading(false);
    }
  };

  const showNotice = (content, color = "success") => {
    setMessage(content);
    setNotiColor(color);
    setNotification(true);
    setTimeout(() => {
      setNotification(false);
      setMessage("");
      setNotiColor("success");
    }, 2500);
  };

  const loadReport = () => {
    if (init() !== "success") {
      window.location.assign("/signin");
      return;
    }

    setLoading(true);

    axios
      .get(Constants.getUrls.getUserAttemptReports, {
        params: {
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
          search: appliedFilters.search,
          practice_page: pages.practice,
          practice_limit: limits.practice,
          mock_page: pages.mock,
          mock_limit: limits.mock,
          test_series_page: pages.testSeries,
          test_series_limit: limits.testSeries,
          live_test_page: pages.liveTest,
          live_test_limit: limits.liveTest,
        },
      })
      .then((resp) => {
        if (resp?.data?.status === "success") {
          setReport(resp?.data?.data || null);
        } else {
          showNotice(resp?.data?.message || "Unable to load user attempt reports.", "warning");
        }
      })
      .catch((error) => {
        showNotice(
          error?.response?.data?.message || "Unable to load user attempt reports.",
          "error"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    loadReport();
  }, [appliedFilters, pages, limits]);

  const applyFilters = () => {
    setPages({
      practice: 1,
      mock: 1,
      testSeries: 1,
      liveTest: 1,
    });
    setAppliedFilters({
      start_date: filters.start_date || today,
      end_date: filters.end_date || filters.start_date || today,
      search: filters.search.trim(),
    });
  };

  const resetFilters = () => {
    const nextFilters = {
      start_date: today,
      end_date: today,
      search: "",
    };

    setFilters(nextFilters);
    setPages({
      practice: 1,
      mock: 1,
      testSeries: 1,
      liveTest: 1,
    });
    setAppliedFilters(nextFilters);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox
          mb={2}
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(6, minmax(0, 1fr))",
          }}
          gap={2}
        >
          {summaryCards.map((item) => (
            <Card
              key={item.key}
              sx={{
                background:
                  "linear-gradient(180deg, rgba(13,25,40,0.96) 0%, rgba(10,20,33,0.94) 100%)",
                border: "1px solid rgba(126, 170, 220, 0.12)",
                boxShadow: "0 18px 40px rgba(0,0,0,0.2)",
              }}
            >
              <MDBox p={2}>
                <MDTypography variant="button" sx={{ color: "#8fa7c4" }}>
                  {item.label}
                </MDTypography>
                <MDTypography
                  variant="h4"
                  sx={{
                    color:
                      item.color === "primary"
                        ? "#60a5fa"
                        : item.color === "info"
                          ? "#38bdf8"
                          : item.color === "warning"
                            ? "#f59e0b"
                            : item.color === "success"
                              ? "#34d399"
                              : item.color === "error"
                                ? "#f87171"
                                : "#edf4ff",
                  }}
                >
                  {item.key === "total_points"
                    ? `${formatPoints(report?.summary?.[item.key] || 0)}`
                    : report?.summary?.[item.key] || 0}
                </MDTypography>
              </MDBox>
            </Card>
          ))}
        </MDBox>

        <Card
          sx={{
            mb: 3,
            background: "linear-gradient(180deg, rgba(11,22,36,0.96) 0%, rgba(9,18,31,0.96) 100%)",
            border: "1px solid rgba(126, 170, 220, 0.12)",
            boxShadow: "0 18px 40px rgba(0,0,0,0.22)",
          }}
        >
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
          >
            <MDTypography variant="h6" color="white">
              User Test Activity Reports
            </MDTypography>
          </MDBox>

          <MDBox p={2}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={filters.start_date}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, start_date: event.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={filters.end_date}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, end_date: event.target.value }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search by user, test or subject"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, search: event.target.value }))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      applyFilters();
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <MDBox display="flex" gap={1} justifyContent={{ xs: "stretch", md: "flex-end" }}>
                  <MDButton variant="contained" color="info" onClick={applyFilters}>
                    Apply
                  </MDButton>
                  <MDButton variant="outlined" color="dark" onClick={resetFilters}>
                    Reset
                  </MDButton>
                </MDBox>
              </Grid>
            </Grid>

            <MDBox mt={2}>
              <MDTypography variant="caption" color="text">
                Showing attempts from {report?.filters?.start_date || appliedFilters.start_date} to{" "}
                {report?.filters?.end_date || appliedFilters.end_date}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        {loading ? (
          <Card
            sx={{
              background:
                "linear-gradient(180deg, rgba(11,22,36,0.96) 0%, rgba(9,18,31,0.96) 100%)",
              border: "1px solid rgba(126, 170, 220, 0.12)",
            }}
          >
            <MDBox p={4} textAlign="center">
              <MDTypography variant="h5" sx={{ color: "#edf4ff" }}>
                Loading activity reports...
              </MDTypography>
            </MDBox>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <AttemptSection
                title="Practice Tests Solved"
                rows={report?.practice_tests || []}
                pagination={report?.practice_tests_pagination}
                onPageChange={(page) => setPages((prev) => ({ ...prev, practice: page }))}
                onEntriesPerPageChange={(limit) => {
                  setLimits((prev) => ({ ...prev, practice: limit }));
                  setPages((prev) => ({ ...prev, practice: 1 }));
                }}
                emptyText="No practice tests solved in the selected date range."
                onViewAttempt={(row) => openAttempt("practice", row)}
                renderMeta={(row) =>
                  `${row.subject_name} • ${row.topic_name} • ${row.level_name}`
                }
              />
            </Grid>

            <Grid item xs={12}>
              <AttemptSection
                title="Mock Tests Solved"
                rows={report?.mock_tests || []}
                pagination={report?.mock_tests_pagination}
                onPageChange={(page) => setPages((prev) => ({ ...prev, mock: page }))}
                onEntriesPerPageChange={(limit) => {
                  setLimits((prev) => ({ ...prev, mock: limit }));
                  setPages((prev) => ({ ...prev, mock: 1 }));
                }}
                emptyText="No mock tests solved in the selected date range."
                onViewAttempt={(row) => openAttempt("mock", row)}
                renderMeta={(row) =>
                  `Payment: ${row.payment_type} • Status: ${row.test_status || "-"}${
                    row.rank ? ` • Rank: ${row.rank}` : ""
                  }`
                }
              />
            </Grid>

            <Grid item xs={12}>
              <AttemptSection
                title="Test Series Solved"
                rows={report?.test_series_attempts || []}
                pagination={report?.test_series_attempts_pagination}
                onPageChange={(page) => setPages((prev) => ({ ...prev, testSeries: page }))}
                onEntriesPerPageChange={(limit) => {
                  setLimits((prev) => ({ ...prev, testSeries: limit }));
                  setPages((prev) => ({ ...prev, testSeries: 1 }));
                }}
                emptyText="No test series solved in the selected date range."
                onViewAttempt={(row) => openAttempt("series", row)}
                renderMeta={(row) =>
                  `Payment: ${row.payment_type} • Status: ${row.test_status || "-"}${
                    row.rank ? ` • Rank: ${row.rank}` : ""
                  }`
                }
              />
            </Grid>

            <Grid item xs={12}>
              <AttemptSection
                title="Live Tests Solved"
                rows={report?.live_test_attempts || []}
                pagination={report?.live_test_attempts_pagination}
                onPageChange={(page) => setPages((prev) => ({ ...prev, liveTest: page }))}
                onEntriesPerPageChange={(limit) => {
                  setLimits((prev) => ({ ...prev, liveTest: limit }));
                  setPages((prev) => ({ ...prev, liveTest: 1 }));
                }}
                emptyText="No live tests solved in the selected date range."
                onViewAttempt={(row) => openAttempt("live", row)}
                renderMeta={(row) =>
                  `Payment: ${row.payment_type} • Status: ${row.test_status || "-"}${
                    row.rank ? ` • Rank: ${row.rank}` : ""
                  }`
                }
              />
            </Grid>
          </Grid>
        )}
      </MDBox>

      <AttemptReviewDialog
        open={reviewOpen}
        onClose={closeReview}
        loading={reviewLoading}
        error={reviewError}
        review={review}
        userName={reviewUserName}
      />

      <MDSnackbar
        color={notiColor}
        icon="notifications"
        title="AgriQuiz"
        content={message}
        open={notification}
        onClose={() => setNotification(false)}
        close={() => setNotification(false)}
        bgWhite
      />
    </DashboardLayout>
  );
}
